import type { CollaborationNotesResponse, SubagentsResponse } from "./types";

type Agent = SubagentsResponse["subagents"][number];
type CollaborationNote = CollaborationNotesResponse["notes"][number];

const ACTIVE_WITHOUT_REPORTS_WARN_MS = 30 * 60 * 1000;
const TRANSIENT_MISSION_RE =
  /\b(?:critical|immediate|immediately|backlog|unhandled|market-event-router|clear\b.{0,80}\bmessages?)\b/i;

export interface DismissedRosterItem {
  id: string;
  name: string;
  role: string;
  reason: string;
  dismissedAt?: string;
  identity?: Agent["identity"];
}

export interface RosterHealthSummary {
  warningCount: number;
  activeWithoutReportsCount: number;
  activeStaffLensReadyCount: number;
  transientMissionCount: number;
  reviewIds: {
    activeWithoutReports: string[];
    transientMissions: string[];
  };
}

function timestampMs(value?: string) {
  const parsed = Date.parse(value ?? "");
  return Number.isFinite(parsed) ? parsed : null;
}

function lastAgentActivityMs(agent: Agent) {
  const reports = Array.isArray(agent.reports) ? agent.reports : [];
  const redirections = Array.isArray(agent.redirections) ? agent.redirections : [];
  const lastReport = reports.at(-1);
  const lastRedirection = redirections.at(-1);
  return (
    timestampMs(agent.lastActivityAt) ??
    timestampMs(lastReport?.reportedAt) ??
    timestampMs(lastRedirection?.redirectedAt) ??
    timestampMs(agent.createdAt)
  );
}

export function summarizeRosterHealth(
  agents: Agent[],
  nowMs = Date.now(),
  options: { executionDelegateId?: string; collaborationNotes?: CollaborationNote[] } = {},
): RosterHealthSummary {
  const active = agents.filter((agent) => agent.status === "active");
  const collabActiveAgentIds = new Set(
    (options.collaborationNotes ?? []).map((note) => note.verdict?.decidedBy).filter(Boolean),
  );
  const activeStaffLensReady = active.filter(
    (agent) => agent.id !== options.executionDelegateId && agent.mission.trim().length > 0,
  );
  const activeWithoutReports = active.filter((agent) => {
    if (collabActiveAgentIds.has(agent.id)) return false;
    const reports = Array.isArray(agent.reports) ? agent.reports : [];
    const lastActivityMs = lastAgentActivityMs(agent);
    const ageMs = lastActivityMs === null ? 0 : Math.max(0, nowMs - lastActivityMs);
    return reports.length === 0 && ageMs >= ACTIVE_WITHOUT_REPORTS_WARN_MS;
  });
  const transientMissions = active.filter((agent) => TRANSIENT_MISSION_RE.test(agent.mission));
  return {
    warningCount:
      (activeWithoutReports.length > 0 ? 1 : 0) + (transientMissions.length > 0 ? 1 : 0),
    activeWithoutReportsCount: activeWithoutReports.length,
    activeStaffLensReadyCount: activeStaffLensReady.length,
    transientMissionCount: transientMissions.length,
    reviewIds: {
      activeWithoutReports: activeWithoutReports.slice(0, 3).map((agent) => agent.id),
      transientMissions: transientMissions.slice(0, 3).map((agent) => agent.id),
    },
  };
}

export function visibleAgentRoleFromMission(mission: string, isDelegate = false) {
  const normalized = mission.toLowerCase();
  if (isDelegate) return "실행 담당";
  if (
    normalized.includes("market") ||
    normalized.includes("rumor") ||
    normalized.includes("source") ||
    normalized.includes("시장")
  )
    return "시장 정보";
  if (
    normalized.includes("execution") ||
    normalized.includes("ledger") ||
    normalized.includes("audit") ||
    normalized.includes("체결")
  )
    return "실행 감사";
  if (normalized.includes("risk")) return "위험 검토";
  if (normalized.includes("research")) return "전략 조사";
  return "전략 보조";
}

function compactDismissalReason(reason: string | undefined) {
  const normalized = reason?.toLowerCase() ?? "";
  if (/unresponsive|non-response|unhandled|응답/.test(normalized)) return "응답 없음";
  if (/performance|decorative|성과|반복|failed/.test(normalized)) return "성과 미달";
  if (/tool|submit_trade_intent|권한|delegate/.test(normalized)) return "도구 문제";
  return "업무 종료";
}

export function buildDismissedRoster(agents: Agent[]): DismissedRosterItem[] {
  return agents
    .filter((agent) => agent.status === "terminated")
    .map((agent) => ({
      id: agent.id,
      name: agent.identity?.name?.trim() || visibleAgentRoleFromMission(agent.mission),
      role: visibleAgentRoleFromMission(agent.mission),
      reason: compactDismissalReason(agent.terminatedReason),
      dismissedAt: agent.lastActivityAt,
      identity: agent.identity,
    }))
    .sort((left, right) =>
      String(right.dismissedAt ?? "").localeCompare(String(left.dismissedAt ?? "")),
    );
}
